// src/Pages/Home/HomePage.jsx

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Keyboard } from "swiper/modules";
import { m } from "framer-motion";

import SharedLayout from "../SharedLayout";
import Buttons from "../../Components/Button/Buttons";
import BlogMetro from "../../Components/Blogs/BlogMetro";
import PortfolioColorful from "../../Components/Portfolio/PortfolioColorful";
import UrbanBrewFooter from "../../Components/Footers/UrbanBrewFooter";
import { fadeIn } from "../../Functions/GlobalAnimations";

const heroTitleClass =
  "font-serif font-light text-[40px] xs:text-[34px] sm:text-[38px] md:text-[44px] lg:text-[56px] xl:text-[72px] text-white leading-[1.08] break-words";
const sectionPad =
  "py-[100px] lg:py-[80px] md:py-[70px] sm:py-[60px] xs:py-[50px]";
const sectionPadLarge =
  "py-[120px] lg:py-[100px] md:py-[80px] sm:py-[60px] xs:py-[50px]";

// -----------------------------------------------------------------------------
// HERO SLIDES
// -----------------------------------------------------------------------------
const heroSlides = [
  {
    headline: "Brew. Better.",
    subheadline:
      "Elevate breaks with bean-to-cup quality and service that just works.",
    cta: "Learn More",
    link: "/solutions/brew-better",
    img: "/assets/img/webp/hero-brew-better.webp",
    imgAlt: "UrbanBrew bean-to-cup coffee solution for modern workplaces",
  },
  {
    headline: "Serve. Smarter.",
    subheadline:
      "Seamless vending, smart refills, and SLA-driven service — refreshment made reliable.",
    cta: "Learn More",
    link: "/solutions/serve-smarter",
    img: "/assets/img/webp/hero-serve-smarter.webp",
    imgAlt: "Smart vending and workplace refreshment service by UrbanBrew",
  },
  {
    headline: "Self-Serve. Zero Fuss.",
    subheadline:
      "We deploy the setup, you enjoy 24×7 access. Refills and service? On us.",
    cta: "Learn More",
    link: "/solutions/self-serve",
    img: "/assets/img/webp/hero-selfserve.webp",
    imgAlt: "Self-serve workplace refreshment station with UrbanBrew support",
  },
];

const industriesPortfolioData = [
  {
    title: "Corporate Offices",
    // TODO: Replace with final asset corporate-office-refreshments.webp when available
    img: "/assets/img/webp/home-corporate-hand-crafted-bg.webp",
    imgAlt: "Corporate office refreshment station with premium beverage solutions",
    subtitle: "Beverage stations that boost focus, culture, and collaboration.",
    link: "/solutions/brew-better",
  },
  {
    title: "Institutions",
    // TODO: Replace with final asset institutional-refreshment-solutions.webp when available
    img: "/assets/img/webp/home-consulting-about-bg.webp",
    imgAlt: "Institutional workplace refreshment solutions for staff and visitors",
    subtitle: "Reliable refreshment for long-hour staff and visiting professionals.",
    link: "/solutions/instant-machines",
  },
  {
    title: "Hospitality & Retail",
    // TODO: Replace with final asset hospitality-retail-refreshments.webp when available
    img: "/assets/img/webp/home-restaurant-img-transparent-05.webp",
    imgAlt: "Hospitality and retail self-serve refreshment experience",
    subtitle: "Self-serve systems that elevate guest experiences.",
    link: "/solutions/self-serve",
  },
  {
    title: "SMEs & Startups",
    // TODO: Replace with final asset sme-startup-coffee-solutions.webp when available
    img: "/assets/img/webp/home-freelancer-img-05.webp",
    imgAlt: "Affordable coffee and refreshment solutions for growing teams",
    subtitle: "Affordable, scalable setups designed for agile teams.",
    link: "/solutions/bean-to-cup",
  },
];

const HomePage = () => {
  return (
    <SharedLayout activePage="/" title="Urban Brew — Beyond Beverages">
      <div className="bg-[#223bac] overflow-x-hidden">

        {/* ========================================================= */}
        {/* SECTION 1 — HERO */}
        {/* ========================================================= */}
        <section className="p-0 overflow-hidden min-h-screen relative">
          <Swiper
            modules={[Pagination, Keyboard, Autoplay]}
            slidesPerView={1}
            keyboard={{ enabled: true }}
            pagination={{ clickable: true }}
            autoplay={{ delay: 4500, disableOnInteraction: false }}
            className="swiper-pagination-01 swiper-pagination-light hero-slider-vertical-pagination"
          >
            {heroSlides.map((slide, i) => (
              <SwiperSlide
                key={i}
                className="relative min-h-screen overflow-hidden"
                aria-label={slide.imgAlt}
              >
                {slide.img && (
                  <img
                    src={slide.img}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    fetchPriority={i === 0 ? "high" : "low"}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding={i === 0 ? "sync" : "async"}
                    width={1920}
                    height={1080}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-[#223bac] via-[#223bac] via-70% to-[#223bac]/80"></div>

                <Container className="h-full relative z-10 px-4 sm:px-6">
                  <Row className="flex flex-col justify-center min-h-[85vh] md:min-h-[90vh] py-24 md:py-16 lg:py-20">
                    <Col lg={8} md={10} className="flex flex-col justify-center">
                      <m.h1
                        className={heroTitleClass}
                        {...fadeIn}
                      >
                        <span className="font-semibold block">
                          {slide.headline}
                        </span>
                      </m.h1>

                      <m.p
                        {...fadeIn}
                        transition={{ delay: 0.3 }}
                        className="text-white/90 text-base sm:text-lg md:text-xl mt-4 max-w-[600px] font-serif"
                      >
                        {slide.subheadline}
                      </m.p>

                      <m.div {...fadeIn} transition={{ delay: 0.6 }} className="mt-8">
                        <Link
                          aria-label={slide.cta}
                          to={slide.link}
                          className="inline-block"
                        >
                          <span className="flex items-center hover:text-white">
                            <span className="w-[50px] h-[50px] flex justify-center items-center rounded-full border-[2px] border-[#ffffff4d] mr-[20px]">
                              <i className="fas fa-arrow-right text-white"></i>
                            </span>
                            <span className="text-white font-medium text-xmd font-serif uppercase">
                              {slide.cta}
                            </span>
                          </span>
                        </Link>
                      </m.div>
                    </Col>
                  </Row>
                </Container>
              </SwiperSlide>
            ))}
          </Swiper>
        </section>

        {/* ========================================================= */}
        {/* SECTION 2 — ABOUT US (Scroll Animation) */}
        {/* ========================================================= */}
        <section
          id="about"
          className={`flex flex-col justify-center ${sectionPad} bg-white text-[#22429a]`}
        >
          <Container>
            <Row className="items-start mb-10 md:mb-[70px] g-4">
              <Col md={5}>
                <m.span
                  {...fadeIn}
                  className="font-bold text-[#22429a] uppercase tracking-[1px] block sm:mb-[20px]"
                >
                  A Decade of Trust
                </m.span>

                <m.p
                  {...fadeIn}
                  transition={{ delay: 0.2 }}
                  className="text-[#22429a] text-base leading-[28px] font-serif"
                >
                  From the legacy of Urban Beverages, Urban Brew makes every break
                  effortless and memorable.
                </m.p>
              </Col>

              <Col md={7}>
                <m.h2
                  {...fadeIn}
                  transition={{ delay: 0.4 }}
                  className="heading-5 font-serif font-light leading-[32px] sm:leading-[38px] md:leading-[44px] mb-[15px]"
                >
                  We’re redefining workplace refreshments with{" "}
                  <span className="font-semibold text-[#223bac]">
                    smarter systems, premium beverages, and people-first service.
                  </span>
                </m.h2>
              </Col>
            </Row>

            <Row className="justify-center text-center g-4">
              {[
                {
                  icon: "fa-solid fa-mug-hot",
                  title: "Experience",
                  text: "Over ten years of serving enterprises with reliability and care.",
                },
                {
                  icon: "fas fa-users",
                  title: "People-First",
                  text: "Designed around teams, clients, and guests — because service matters.",
                },
                {
                  icon: "fa-solid fa-headset",
                  title: "Support",
                  text: "SLA-backed assistance ensuring every cup flows seamlessly.",
                },
              ].map((item, i) => (
                <Col xs={12} sm={6} lg={4} className="mb-2 sm:mb-4 lg:mb-0" key={i}>
                  <m.div
                    {...fadeIn}
                    transition={{ delay: 0.2 + i * 0.15 }}
                    className="group bg-[#f9fbff] hover:bg-white border border-[#e4ebf7] rounded-2xl p-6 md:p-10 h-full transition-all hover:shadow-[0_8px_24px_rgba(34,66,154,0.08)] md:hover:-translate-y-2"
                  >
                    <i
                      className={`${item.icon} text-[#223bac] text-[42px] mb-6`}
                    ></i>
                    <h4 className="font-serif font-semibold text-[18px] mb-3 text-[#223bac]">
                      {item.title}
                    </h4>
                    <p className="text-[#22429a]/80 text-[15px] leading-[26px] font-serif">
                      {item.text}
                    </p>
                  </m.div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ========================================================= */}
        {/* SECTION 3 — BRAND TRUST (Animated Titles) */}
        {/* ========================================================= */}
        <section className={`${sectionPadLarge} bg-[#223bac] text-white`}>
          <Container>
            <Row className="mb-10 md:mb-16 g-4">
              <Col md={5}>
                <m.span
                  {...fadeIn}
                  className="font-serif font-bold uppercase tracking-[2px]"
                >
                  Trusted by Enterprises
                </m.span>
              </Col>

              <Col md={7}>
                <m.h2
                  {...fadeIn}
                  transition={{ delay: 0.2 }}
                  className="heading-5 font-serif font-light"
                >
                  Trusted. Tried.{" "}
                  <span className="font-semibold">Brewed.</span>
                </m.h2>
                <m.p
                  {...fadeIn}
                  transition={{ delay: 0.35 }}
                  className="text-white/90 max-w-[520px] leading-[28px] font-serif"
                >
                  From small teams to large institutions, Urban Brew keeps
                  workplaces refreshed with systems they can rely on — every day.
                </m.p>
              </Col>
            </Row>

            {/* INDUSTRY GRID */}
            <m.div {...fadeIn} transition={{ delay: 0.4 }}>
              <PortfolioColorful
                staticGrid
                className="m-0"
                overlay="#223bacd9"
                grid="home-static-grid home-portfolio-grid"
                data={industriesPortfolioData}
              />
            </m.div>
          </Container>
        </section>

        {/* ========================================================= */}
        {/* SECTION 4 — OUR VALUES (Refined + Animated) */}
        {/* ========================================================= */}
        <section className={`flex flex-col justify-center ${sectionPad} bg-white text-[#22429a]`}>
          <Container>
            <Row>
              <Col md={5}>
                <m.span
                  {...fadeIn}
                  className="font-bold text-[#22429a] uppercase tracking-[1px] block sm:mb-[20px]"
                >
                  Why Urban Brew?
                </m.span>
              </Col>

              <Col md={7}>
                <m.h2
                  {...fadeIn}
                  transition={{ delay: 0.2 }}
                  className="heading-5 font-serif leading-[34px] sm:leading-[42px] md:leading-[50px] mb-8 md:mb-[60px]"
                >
                  Creating{" "}
                  <span className="font-semibold">
                    smarter, happier workplaces
                  </span>{" "}
                  through seamless refreshment experiences.
                </m.h2>

                <Row className="g-4">
                  <Col md={6}>
                    <m.div {...fadeIn} transition={{ delay: 0.35 }}>
                      <span className="font-bold text-[#22429a] text-lg font-serif block mb-[15px]">
                        Our Purpose
                      </span>
                      <p className="font-serif leading-[28px]">
                        Enhancing employee well-being and productivity by making
                        refreshment management effortless.
                      </p>
                    </m.div>
                  </Col>

                  <Col md={6}>
                    <m.div {...fadeIn} transition={{ delay: 0.45 }}>
                      <span className="font-bold text-[#22429a] text-lg font-serif block mb-[15px]">
                        Our Promise
                      </span>
                      <p className="font-serif leading-[28px]">
                        Consistency, reliability, sustainability — so your team
                        enjoys seamless beverage experiences every day.
                      </p>
                    </m.div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </section>

        {/* ========================================================= */}
        {/* SECTION 5 — BLOG METRO */}
        {/* ========================================================= */}
        <section id="insights" className={`${sectionPadLarge} bg-[#223bac] text-white`}>
          <Container>
            <Row className="mb-10 md:mb-[80px] justify-between g-4">
              <Col xs={12} md="auto">
                <m.span
                  {...fadeIn}
                  className="font-serif font-bold uppercase tracking-[2px]"
                >
                  Insights & Ideas
                </m.span>

                <m.h2
                  {...fadeIn}
                  transition={{ delay: 0.2 }}
                  className="heading-5 font-serif font-light mt-[15px]"
                >
                  Fresh Perspectives.{" "}
                  <span className="font-semibold">Bold Brews.</span>
                </m.h2>

                <m.p
                  {...fadeIn}
                  transition={{ delay: 0.35 }}
                  className="text-white max-w-[520px] mt-[15px] leading-[28px] font-serif"
                >
                  Stories and insights on how great coffee and better service fuel
                  stronger workplaces.
                </m.p>
              </Col>

              <Col xs={12} md="auto" className="flex items-start md:items-center">
                <m.div {...fadeIn} transition={{ delay: 0.45 }}>
                  <Buttons
                    ariaLabel="blog-metro"
                    to="/articles"
                    className="font-medium font-serif uppercase btn-link after:h-[1px] after:bg-white text-white hover:text-white hover:opacity-100"
                    size="xl"
                    title="Explore More"
                  />
                </m.div>
              </Col>
            </Row>
          </Container>

          <m.div {...fadeIn} transition={{ delay: 0.55 }}>
            <Container className="px-4 sm:px-6">
              <BlogMetro
                staticGrid
                grid="home-static-grid home-blog-grid"
                data={[
                  {
                    img: "/assets/img/webp/blog-productivity-hero.webp",
                    imgAlt:
                      "Workplace refreshments supporting team productivity and wellbeing",
                    title: "Why Workplace Refreshments Matter More Than Ever",
                    category: ["Work Culture"],
                    content:
                      "How planned refreshment services enhance workplace wellbeing and productivity.",
                    link: "/articles/why-workplace-refreshments-matter",
                  },
                  {
                    img: "/assets/img/webp/blog-automation-hero.webp",
                    imgAlt: "Smart vending technology for workplace refreshment management",
                    title: "How Refreshments Influence Team Engagement",
                    category: ["Team Engagement"],
                    content:
                      "Learn how quality refreshments increase engagement and positive workplace culture.",
                    link: "/articles/refreshments-influence-team-engagement",
                  },
                  {
                    img: "/assets/img/webp/blog-experience-hero.webp",
                    imgAlt: "Visitor hospitality and premium coffee service in the office",
                    title: "Creating a Hospitality-First Office Culture",
                    category: ["Hospitality"],
                    content:
                      "How offices can adopt hospitality mindset with simple refreshment strategies.",
                    link: "/articles/creating-hospitality-first-office-culture",
                  },
                  {
                    img: "/assets/img/webp/blog-ally-hero.webp",
                    imgAlt: "UrbanBrew insights on workplace culture and refreshment",
                    title: "Explore More Insights",
                    category: ["Insights"],
                    content:
                      "Stories and ideas on coffee, service, and stronger workplaces.",
                    link: "/articles",
                  },
                ]}
              />
            </Container>
          </m.div>
        </section>

        {/* ========================================================= */}
        {/* SECTION 6 — CTA */}
        {/* ========================================================= */}
        <section className={`${sectionPad} bg-white text-center`}>
          <Container>
            <m.h2
              {...fadeIn}
              className="heading-5 font-serif font-light text-[#22429a] mb-6 max-w-[800px] mx-auto"
            >
              Ready to elevate your workplace experience?
            </m.h2>
            <m.div {...fadeIn} transition={{ delay: 0.2 }}>
              <Link to="/contact" className="ub-primary-btn">
                Let&apos;s Get Started
              </Link>
            </m.div>
          </Container>
        </section>

        <UrbanBrewFooter />
      </div>
    </SharedLayout>
  );
};

export default HomePage;
